import { useRef, useState } from "react";
import { UploadCloud, Loader2, Trash2, RefreshCw, AlertTriangle } from "lucide-react";
import { uploadGeoImage, geoImageUrl } from "../../../api/geo/geoImage";
import { useToast } from "../../../context/ToastContext";
import styles from "./GeoImageUpload.module.css";

const MIN_WIDTH = 800;
const MIN_HEIGHT = 600;

const readImageDimensions = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

const GeoImageUpload = ({ value, onChange }) => {
  const { showToast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dimWarning, setDimWarning] = useState("");

  const pick = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    setDimWarning("");

    const dims = await readImageDimensions(file);
    if (dims && (dims.width < MIN_WIDTH || dims.height < MIN_HEIGHT)) {
      setDimWarning(`Image is ${dims.width}×${dims.height}px — for best quality use at least ${MIN_WIDTH}×${MIN_HEIGHT}px.`);
    }

    setUploading(true);
    try {
      const res = await uploadGeoImage(file);
      if (res?.success && res.data?.imageUrl) {
        onChange(res.data.imageUrl);
        showToast("Image uploaded", "success");
      } else {
        showToast(res?.message || "Upload failed", "error");
      }
    } catch (e) {
      showToast(e.response?.data?.message || "Image upload failed", "error");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className={styles.wrap}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
        className={styles.hiddenInput}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {value ? (
        <div className={styles.preview}>
          <img
            src={geoImageUrl(value)}
            alt="Main"
            className={styles.thumb}
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
          />
          {dimWarning && (
            <div className={styles.dimWarning}>
              <AlertTriangle size={14} />
              <span>{dimWarning}</span>
            </div>
          )}
          <div className={styles.previewInfo}>
            <span className={styles.previewName}>Main image set</span>
            <div className={styles.previewActions}>
              <button type="button" className={styles.changeBtn} onClick={pick} disabled={uploading}>
                {uploading ? <Loader2 size={13} className={styles.spin} /> : <RefreshCw size={13} />}
                {uploading ? "Uploading…" : "Change"}
              </button>
              <button type="button" className={styles.removeBtn} onClick={() => { onChange(""); setDimWarning(""); }}>
                <Trash2 size={13} /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button type="button" className={styles.dropzone} onClick={pick} disabled={uploading}>
          <span className={styles.dzIcon}>
            {uploading ? <Loader2 size={20} className={styles.spin} /> : <UploadCloud size={20} />}
          </span>
          <span className={styles.dzTitle}>{uploading ? "Uploading…" : "Click to upload an image"}</span>
          <span className={styles.dzHint}>PNG, JPG or WebP · at least {MIN_WIDTH}×{MIN_HEIGHT}px · up to 5&nbsp;MB</span>
        </button>
      )}
    </div>
  );
};

export default GeoImageUpload;
